import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { aoiApi, type AoiListItem } from '../services/aoiApi';
import { PinAoiModal } from '../components/PinAoiModal';

export function AoisList() {
  const nav = useNavigate();
  const [items, setItems] = useState<AoiListItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pinOpen, setPinOpen] = useState(false);

  async function reload() {
    try {
      const r = await aoiApi.list();
      setItems(r.aois);
      setErr(null);
    } catch (e: any) {
      setErr(e?.message ?? 'Failed to load');
    }
  }
  useEffect(() => { reload(); }, []);

  return (
    <div className="px-8 py-7 max-w-[1280px] mx-auto">
      <header className="flex items-end justify-between pb-6 border-b border-border">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">workspace</div>
          <h1 className="font-display text-[34px] leading-tight">
            Areas of <em className="text-accent not-italic">interest</em>
          </h1>
          <div className="text-[12px] text-text-dim mt-1.5 font-mono">
            {items === null ? 'loading…' : `${items.length} pinned · weld county only · v0.1`}
          </div>
        </div>
        <button
          onClick={() => setPinOpen(true)}
          className="px-4 py-2 bg-accent text-bg text-[13px] font-medium rounded hover:bg-accent-bright"
        >+ Pin new AOI</button>
      </header>

      <div className="mt-7 flex items-center justify-between">
        <div>
          <h2 className="font-display text-[18px]">Pinned · <em className="text-accent not-italic">working</em></h2>
          <div className="text-[11px] text-text-faint font-mono mt-0.5">your active set · click to open</div>
        </div>
      </div>

      <div className="mt-3 bg-bg-card-soft border border-border rounded overflow-hidden">
        {err && (
          <div className="px-5 py-4 text-[12px] text-red bg-red/10 border-b border-border">{err}</div>
        )}
        {items === null && !err && (
          <div className="px-5 py-8 text-[12px] text-text-faint font-mono">loading AOIs…</div>
        )}
        {items !== null && items.length === 0 && (
          <div className="px-5 py-8 text-center">
            <div className="text-[13px] text-text-dim">No AOIs pinned yet.</div>
            <div className="text-[11px] text-text-faint font-mono mt-1">Pin a TRS tract to see its grantor roster.</div>
          </div>
        )}
        {items?.map((aoi, i) => (
          <Link
            key={aoi.id}
            to={`/mi/aoi/${aoi.id}`}
            className="grid grid-cols-[40px_2fr_1.2fr_1fr_1fr_60px_auto] items-center gap-4 px-5 py-4 border-b border-border last:border-b-0 hover:bg-bg-hover/40 transition-colors"
          >
            <div className="font-mono text-[13px] text-text-faint">{String(i + 1).padStart(2, '0')}</div>
            <div>
              <div className="font-display text-[16px] text-text">{aoi.nickname ?? aoi.name}</div>
              <div className="text-[11px] text-text-dim font-mono mt-0.5">
                {aoi.nickname && <span className="text-text-faint">{aoi.name} · </span>}
                {aoi.basin ?? <span className="text-text-ghost">no basin</span>}
                {aoi.trs_keys.length > 1 && <span className="text-text-ghost"> · {aoi.trs_keys.length} tracts</span>}
              </div>
            </div>
            <div>
              <div className="text-[12px] text-text-ghost italic">—</div>
              <div className="text-[10px] text-text-faint font-mono mt-0.5">signals · session 7</div>
            </div>
            <div>
              <div className="text-[12px] text-text-ghost italic">—</div>
              <div className="text-[10px] text-text-faint font-mono mt-0.5">operator · pipeline-derived</div>
            </div>
            <div>
              <div className="text-[14px] text-text">
                <em className="text-accent not-italic">{aoi.party_count}</em> parties
              </div>
              <div className="text-[10px] text-text-faint font-mono mt-0.5">
                {aoi.doc_count} docs
                {aoi.last_recording_date && <> · last {relativeDate(aoi.last_recording_date)}</>}
              </div>
            </div>
            <div className="text-text-ghost italic text-[12px]">—</div>
            <div>
              <button className="px-3 py-1.5 border border-accent text-accent text-[12px] rounded hover:bg-accent-soft" onClick={e => { e.preventDefault(); nav(`/mi/aoi/${aoi.id}`); }}>Open →</button>
            </div>
          </Link>
        ))}
      </div>

      {/* Recommended-by-Claude section deliberately hidden until session 7 wires up the weekly pass. */}

      {pinOpen && (
        <PinAoiModal
          onClose={() => setPinOpen(false)}
          onPinned={(id) => { setPinOpen(false); nav(`/mi/aoi/${id}`); }}
        />
      )}
    </div>
  );
}

function relativeDate(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const days = Math.floor((now - then) / 86400_000);
  if (days < 0) return 'in future';
  if (days === 0) return 'today';
  if (days === 1) return '1d ago';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}
